/**
 * VersionTimelineDrawer — Cluster H.3 (Feb 2026).
 *
 * Slide-in drawer that lists the past draft versions for the current
 * matter, with one-click restore + side-by-side diff.
 *
 * Endpoints:
 *   GET  /api/v2/agent/sessions/:id/versions
 *   GET  /api/v2/agent/sessions/:id/versions/:vid
 *   POST /api/v2/agent/sessions/:id/versions/:vid/restore
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X as XIcon, History, RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function lineDiff(a, b) {
  const aLines = (a || '').split(/\r?\n/);
  const bLines = (b || '').split(/\r?\n/);
  const max = Math.max(aLines.length, bLines.length);
  const out = [];
  for (let i = 0; i < max; i += 1) {
    const al = aLines[i] ?? '';
    const bl = bLines[i] ?? '';
    if (al === bl) {
      out.push({ kind: 'same', a: al, b: bl, idx: i });
    } else {
      out.push({ kind: 'diff', a: al, b: bl, idx: i });
    }
  }
  return out;
}

export const VersionTimelineDrawer = ({
  open, onClose, sessionId, currentContent, onRestored, isArabic,
}) => {
  const { session } = useAuth();
  const token = session?.access_token;
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedContent, setSelectedContent] = useState('');

  const headers = useMemo(() => (
    token ? { Authorization: `Bearer ${token}` } : {}
  ), [token]);

  useEffect(() => {
    if (!open || !sessionId || !token) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/v2/agent/sessions/${encodeURIComponent(sessionId)}/versions`,
          { headers, timeout: 6000 },
        );
        if (!cancelled) setVersions(res.data?.versions || []);
      } catch (_e) {
        if (!cancelled) setVersions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, token, headers]);

  const loadVersionContent = async (versionId) => {
    if (!sessionId || !token) return;
    setSelected(versionId);
    setSelectedContent('');
    try {
      const res = await axios.get(
        `${API}/v2/agent/sessions/${encodeURIComponent(sessionId)}/versions/${encodeURIComponent(versionId)}`,
        { headers, timeout: 6000 },
      );
      setSelectedContent(res.data?.content || '');
    } catch (_e) {
      setSelectedContent('');
    }
  };

  const restoreVersion = async () => {
    if (!sessionId || !selected || !token) return;
    try {
      const res = await axios.post(
        `${API}/v2/agent/sessions/${encodeURIComponent(sessionId)}/versions/${encodeURIComponent(selected)}/restore`,
        {},
        { headers, timeout: 8000 },
      );
      onRestored?.(res.data?.content || '');
      onClose?.();
    } catch (_e) {
      // toast at caller — drawer just stays open
    }
  };

  const diff = useMemo(
    () => (selected ? lineDiff(selectedContent, currentContent || '') : []),
    [selected, selectedContent, currentContent],
  );

  if (!open) return null;

  const changeCount = diff.filter((d) => d.kind === 'diff').length;

  return (
    <div
      data-testid="version-timeline-drawer"
      style={{
        position: 'fixed',
        top: 0,
        right: isArabic ? undefined : 0,
        left: isArabic ? 0 : undefined,
        bottom: 0,
        width: 'min(720px, 95vw)',
        background: '#0e0e0e',
        color: 'rgba(255,255,255,0.92)',
        borderLeft: isArabic ? undefined : '1px solid #222',
        borderRight: isArabic ? '1px solid #222' : undefined,
        boxShadow: '-12px 0 28px rgba(0,0,0,0.4)',
        zIndex: 80,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
      }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <header
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #1f1f1f',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <History size={16} color="#d6b27d" />
        <div style={{ fontWeight: 500, fontSize: 14 }}>
          {isArabic ? 'سجل الإصدارات' : 'Version timeline'}
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onClose}
          data-testid="version-timeline-close"
          style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 4 }}
          aria-label="close"
        >
          <XIcon size={18} />
        </button>
      </header>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* version list */}
        <aside
          style={{
            width: 240,
            borderRight: isArabic ? undefined : '1px solid #1f1f1f',
            borderLeft: isArabic ? '1px solid #1f1f1f' : undefined,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div data-testid="version-timeline-loading" style={{ padding: 14, fontSize: 12, color: '#b5b5b5' }}>
              {isArabic ? 'يتم التحميل…' : 'Loading…'}
            </div>
          )}
          {!loading && versions.length === 0 && (
            <div data-testid="version-timeline-empty" style={{ padding: 14, fontSize: 12, color: '#b5b5b5' }}>
              {isArabic ? 'لا توجد إصدارات سابقة بعد. سيتم حفظ نسخة عند كل عملية تحرير وحفظ.' : 'No prior versions yet. A snapshot is saved every time you edit and save.'}
            </div>
          )}
          {versions.map((v) => (
            <button
              key={v.version_id}
              type="button"
              onClick={() => loadVersionContent(v.version_id)}
              data-testid={`version-timeline-item-${v.version_id}`}
              style={{
                display: 'block',
                width: '100%',
                textAlign: isArabic ? 'right' : 'left',
                background: selected === v.version_id ? '#1a1a1a' : 'transparent',
                border: 'none',
                borderBottom: '1px solid #181818',
                color: 'inherit',
                cursor: 'pointer',
                padding: '10px 14px',
                fontFamily: 'inherit',
              }}
            >
              <div style={{ fontSize: 12, color: '#d6b27d' }}>{v.label || 'edit'}</div>
              <div style={{ fontSize: 11, color: '#b5b5b5', marginTop: 2 }}>
                {v.created_at ? new Date(v.created_at).toLocaleString(isArabic ? 'ar' : 'en') : ''}
              </div>
              <div style={{ fontSize: 10, color: '#8a8a8a', marginTop: 2 }}>
                {v.length} {isArabic ? 'حرف' : 'chars'}
              </div>
            </button>
          ))}
        </aside>

        {/* diff view */}
        <main style={{ flex: 1, overflow: 'auto', padding: 14 }}>
          {!selected && (
            <div style={{
              // AA on #0e0e0e: #b5b5b5 ≈ 7.6:1
              fontSize: 12, color: '#b5b5b5',
            }}>
              {isArabic ? 'اختر إصداراً لمقارنة التغييرات.' : 'Select a version to compare changes.'}
            </div>
          )}
          {selected && (
            <>
              <div
                data-testid="version-timeline-diff-summary"
                style={{ fontSize: 11, color: '#b5b5b5', marginBottom: 8 }}
              >
                {isArabic
                  ? `${changeCount} سطراً مختلفاً`
                  : `${changeCount} line${changeCount === 1 ? '' : 's'} differ`}
              </div>
              <button
                type="button"
                onClick={restoreVersion}
                data-testid="version-timeline-restore"
                style={{
                  background: '#d6b27d',
                  border: 'none',
                  color: '#0e0e0e',
                  borderRadius: 4,
                  padding: '6px 12px',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontWeight: 500,
                  marginBottom: 12,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <RotateCcw size={13} />
                {isArabic ? 'استعادة هذا الإصدار' : 'Restore this version'}
              </button>
              <pre
                data-testid="version-timeline-diff"
                style={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  background: '#0a0a0a',
                  border: '1px solid #1f1f1f',
                  borderRadius: 4,
                  padding: 10,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                  color: '#aaa',
                }}
              >
                {diff.map((d) => (
                  <div key={d.idx} style={{
                    background: d.kind === 'diff' ? 'rgba(214,178,125,0.08)' : 'transparent',
                    padding: '1px 2px',
                  }}>
                    <span style={{ color: '#e08383' }}>{d.kind === 'diff' ? `- ${d.a}` : `  ${d.a}`}</span>
                    {d.kind === 'diff' && (
                      <span style={{ display: 'block', color: '#83e0a0' }}>{`+ ${d.b}`}</span>
                    )}
                  </div>
                ))}
              </pre>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default VersionTimelineDrawer;
